import React, {useRef, useMemo} from 'react';
import {Box, Flex, Card, chakra, Skeleton, Text} from '@chakra-ui/react';
import {
  AiResearchFieldDefinition,
  LegacyAiResearchFieldDefinition,
} from '../../../shared/fields/enrichment';
import {ObjectId} from 'bson';
import _ from 'lodash';
import {BuilderPreviewAccountCell} from './BuilderPreviewAccountCell';
import {BuilderPreviewJumpToMenu} from './BuilderPreviewJumpToMenu';
import {useEnrichmentPreviewResult} from './hooks/useEnrichmentPreviewResult';
import {BuilderPreviewValue} from './BuilderPreviewValue';

export const BuilderPreviewCardList = ({
  accountIds,
  fieldDefinition,
  isPreviewDisabled = false,
}: {
  accountIds: ObjectId[];
  fieldDefinition?: LegacyAiResearchFieldDefinition | AiResearchFieldDefinition;
  isPreviewDisabled?: boolean;
}) => {
  // Create a ref for the scrollable container
  const containerRef = useRef<HTMLDivElement>(null);

  // Create refs for each account card
  const cardRefs = useMemo(() => {
    return accountIds.reduce<Record<string, React.RefObject<HTMLDivElement>>>(
      (acc, id) => {
        acc[id.toString()] = React.createRef<HTMLDivElement>();
        return acc;
      },
      {}
    );
  }, [accountIds]);

  return (
    <Box flex="1" position="relative" mt="6">
      <Flex inset="0" position="absolute" gap="4">
        {/* Jump To Menu */}
        {accountIds.length > 2 && (
          <BuilderPreviewJumpToMenu
            containerRef={containerRef}
            accountIds={accountIds}
            getItemRef={(id: ObjectId) => cardRefs[id.toString()]}
          />
        )}

        {/* Account Cards */}
        <Flex
          direction="column"
          gap="4"
          flex="1"
          ref={containerRef}
          overflow="auto"
        >
          {accountIds.map((accountId) => (
            <Box
              key={accountId.toString()}
              ref={cardRefs[accountId.toString()]}
            >
              {fieldDefinition ? (
                <BuilderPreviewCard
                  accountId={accountId}
                  fieldDefinition={fieldDefinition}
                  isPreviewDisabled={isPreviewDisabled}
                />
              ) : (
                <StyledCard>
                  <BuilderPreviewAccountCell accountId={accountId} />
                  <Text textColor="kgray.300">-</Text>
                </StyledCard>
              )}
            </Box>
          ))}
        </Flex>
      </Flex>
    </Box>
  );
};

const BuilderPreviewCard = ({
  accountId,
  fieldDefinition,
  isPreviewDisabled,
}: {
  accountId: ObjectId;
  fieldDefinition: LegacyAiResearchFieldDefinition | AiResearchFieldDefinition;
  isPreviewDisabled: boolean;
}) => {
  const {data, failureCount, isFetching, isPending} =
    useEnrichmentPreviewResult({
      accountId,
      fieldDefinitionId: fieldDefinition.id,
      isDisabled: isPreviewDisabled,
    });

  const {value, stale} = data ?? {};

  return (
    <StyledCard>
      <BuilderPreviewAccountCell accountId={accountId} />
      {isPending || (isFetching && failureCount > 0) ? (
        <Skeleton h="6" speed={0} w="40%" />
      ) : (
        <Box whiteSpace="pre-line">
          {value ? <BuilderPreviewValue value={value} isStale={stale} /> : '-'}
        </Box>
      )}
    </StyledCard>
  );
};

const StyledCard = chakra(Card, {
  baseStyle: {
    p: '6',
    boxShadow: 'none',
    border: '1px',
    borderColor: 'kgray.200',
    borderRadius: 'xl',
    gap: '6',
  },
});
